import { jsx } from "panel";
import { ButtonEl } from "common/components/button";
import { InputEl } from "common/components/input";
import { ConfirmEl } from "common/components/confirm";
import { SpinnerEl } from "common/components/spinner";
import { TooltipEl } from "common/components/tooltip";
import { getFormattedPropertyName, getRawPropertyName, getRawPropertyValue, getScdEventValue } from "common/util/format-property";
import { FormattedPropertyMixin } from "common/widgets/profile-activity/formatted-property/template";
import { ButtonTheme } from "common/components/button/constants";
import { Placement } from "common/components/tooltip/types";
import { THEME } from "common/components/confirm/types";
import { ModalEl } from "common/components/modal";
import { Visibility } from "common/components/base/stackable";
import { isScdPropertyName } from "common/report/util/properties";
import { HistoricalPropertyChangeLogEl } from "../historical-property-change-log";
export function template() {
  const {
    t
  } = this.getContext("i18n");
  const {
    editValue,
    isEditing,
    isConfirmingDelete,
    isPropertyChangeLogOpen
  } = this.state;
  const {
    "match-parts": matchParts,
    property,
    loading,
    "sidebar-mode": sidebarMode,
    "can-write-profile": hasWriteProfilePermission,
    "data-group-id": dataGroupId,
    "distinct-id": distinctId
  } = this.attrs();
  const isScdProp = isScdPropertyName(getRawPropertyName(property));
  const propertyEditDisabled = Array.isArray(getRawPropertyValue(property));
  const EditingProperty = () => {
    const controlBarElement = loading ? /* @__PURE__ */ jsx(SpinnerEl, null) : /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(ButtonEl, { sel: ".new-property-button", attrs: {
      theme: ButtonTheme.Tertiary
    }, on: {
      click: this.helpers.handleCancelPropertyEdit
    } }, t("common.cancel")), /* @__PURE__ */ jsx(ButtonEl, { sel: ".new-property-button", attrs: {
      theme: ButtonTheme.Primary
    }, on: {
      click: this.helpers.handleSavePropertyValue
    } }, t("common.save")));
    return /* @__PURE__ */ jsx("div", { sel: ".editing-property", "data-sentry-component": "EditingProperty", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ jsx(InputEl, { sel: ".value-input", attrs: {
      label: getFormattedPropertyName(property)
    }, props: {
      value: editValue
    }, on: {
      input: this.helpers.handleUpdatePropertyValue
    }, hook: {
      insert: this.helpers.inputInserted
    }, "data-sentry-element": "InputEl", "data-sentry-source-file": "template.tsx" }), /* @__PURE__ */ jsx("div", { sel: ".confirmation-buttons" }, controlBarElement));
  };
  const PropertyControls = () => {
    if (loading) {
      return /* @__PURE__ */ jsx("div", { sel: ".loading-spinner-wrapper" }, /* @__PURE__ */ jsx(SpinnerEl, null));
    }
    if (isScdProp) {
      return /* @__PURE__ */ jsx("div", { sel: ".hover-button-wrapper", class: {
        "is-sidebar": sidebarMode
      } }, /* @__PURE__ */ jsx(ButtonEl, { sel: ".control-button", attrs: {
        theme: ButtonTheme.Secondary,
        icon: "time"
      }, on: {
        click: () => this.helpers.handleOpenPropertyChangelog(true)
      } }, "View Changes"));
    }
    if (sidebarMode) {
      return null;
    }
    return /* @__PURE__ */ jsx("div", { sel: ".hover-button-wrapper", "data-sentry-component": "PropertyControls", "data-sentry-source-file": "template.tsx" }, !hasWriteProfilePermission && /* @__PURE__ */ jsx(TooltipEl, { attrs: {
      placement: Placement.Left
    } }, "You don't have permission to edit."), hasWriteProfilePermission && propertyEditDisabled && /* @__PURE__ */ jsx(TooltipEl, { attrs: {
      placement: Placement.Left
    } }, "Editing list properties is disabled."), /* @__PURE__ */ jsx(ButtonEl, { attrs: {
      disabled: propertyEditDisabled || !hasWriteProfilePermission,
      theme: ButtonTheme.Tertiary,
      icon: "edit",
      tabindex: 1
    }, on: {
      click: this.helpers.handleEditProperty
    }, "data-sentry-element": "ButtonEl", "data-sentry-source-file": "template.tsx" }), /* @__PURE__ */ jsx(ButtonEl, { attrs: {
      disabled: !hasWriteProfilePermission,
      theme: ButtonTheme.Tertiary,
      icon: "trashcan",
      tabindex: 2
    }, on: {
      click: this.helpers.handleDeleteButton
    }, "data-sentry-element": "ButtonEl", "data-sentry-source-file": "template.tsx" }));
  };
  const ViewProperty = () => /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", { sel: ".property-wrapper" }, /* @__PURE__ */ jsx(FormattedPropertyMixin, { layout: sidebarMode ? this.helpers.FormattedPropertyLayout.Horizontal : this.helpers.FormattedPropertyLayout.Vertical, property, matchParts, "data-sentry-element": "FormattedPropertyMixin", "data-sentry-source-file": "template.tsx" })), /* @__PURE__ */ jsx(PropertyControls, { "data-sentry-element": "PropertyControls", "data-sentry-source-file": "template.tsx" }));
  const ConfirmDelete = () => /* @__PURE__ */ jsx(ConfirmEl, { attrs: {
    theme: THEME.DELETE,
    "confirm-button-label": "Delete Property",
    open: true
  }, on: {
    submit: this.helpers.handleModalButton
  }, "data-sentry-element": "ConfirmEl", "data-sentry-component": "ConfirmDelete", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ jsx("div", null, /* @__PURE__ */ jsx("div", { sel: ".mp-confirm-title" }, "Delete '", getFormattedPropertyName(property), "' From Profile?"), /* @__PURE__ */ jsx("div", { sel: ".mp-confirm-description" }, "This property will no longer show on this profile.")));
  const PropertyChangeLog = () => {
    return /* @__PURE__ */ jsx(ModalEl, { sel: ".property-change-log-modal", attrs: {
      open: true,
      closeable: true
    }, on: {
      change: (ev) => {
        if (ev.detail.state === Visibility.Closed) {
          this.helpers.handleOpenPropertyChangelog(false);
        }
      }
    }, "data-sentry-element": "ModalEl", "data-sentry-component": "PropertyChangeLog", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ jsx("div", { sel: ".mp-modal-content" }, /* @__PURE__ */ jsx("div", { sel: ".property-change-log-title" }, /* @__PURE__ */ jsx("h2", null, getFormattedPropertyName(property), " [", getScdEventValue(property), "]"), /* @__PURE__ */ jsx("h2", null, "Property changelog")), /* @__PURE__ */ jsx("div", { sel: ".property-change-log-description" }, "Historical properties store changes over time, unlike standard properties which only retain the current value."), /* @__PURE__ */ jsx(HistoricalPropertyChangeLogEl, { dataGroupId, distinctId, property, "data-sentry-element": "HistoricalPropertyChangeLogEl", "data-sentry-source-file": "template.tsx" }), /* @__PURE__ */ jsx("div", { sel: ".property-change-log-done-btn" }, /* @__PURE__ */ jsx(ButtonEl, { attrs: {
      theme: ButtonTheme.Primary
    }, on: {
      click: () => this.helpers.handleOpenPropertyChangelog(false)
    }, "data-sentry-element": "ButtonEl", "data-sentry-source-file": "template.tsx" }, t("common.done")))));
  };
  return /* @__PURE__ */ jsx("div", { sel: ".formatted-property", class: {
    "edit-disabled": propertyEditDisabled || !hasWriteProfilePermission
    /* TODO - for scd prop, cursor should be inherit... */
  }, on: {
    click: isEditing || propertyEditDisabled || !hasWriteProfilePermission || sidebarMode || isScdProp ? null : this.helpers.handleEditProperty
  }, "data-sentry-component": "template", "data-sentry-source-file": "template.tsx" }, isEditing ? /* @__PURE__ */ jsx(EditingProperty, null) : /* @__PURE__ */ jsx(ViewProperty, null), isConfirmingDelete && /* @__PURE__ */ jsx(ConfirmDelete, null), isPropertyChangeLogOpen && /* @__PURE__ */ jsx(PropertyChangeLog, null));
}
